import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useActiveShow } from "common-fe";
import { useTranslation } from "i18n-fe";
import { formatSection, isSectionTestId, showSectionStyle } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { classNumberLabel } from "../util/classDisplay";
import { authoritativeSheetsOnly } from "../util/contenderCompare";
import { formatRideLabel } from "../components/RideLink";
import { MOUSE_ACTIVATION, TOUCH_ACTIVATION } from "../util/sectionsDnd";
import {
  POOL_ID,
  addBar,
  buildItems,
  clearAll,
  diffRows,
  layout,
  makeBarId,
  moveBar,
  moveRider,
  removeBar,
  savedRowsFromRides,
  splitIntoSections,
  toRows,
} from "../util/sectionsModel";

// #3294 -- the equitation sections page. The show office arranges riders into
// sections ahead of the class; every bit of it is optional and the scribe can
// change it at the ring (#3293). One flat scrolling list of dark section bars
// with riders under them, driven by the pure model in util/sectionsModel.js.
// Nothing is written until the secretary acts, and the whole page goes
// read-only only once the class is confirmed and signed (the server refuses
// moves then too, 409 section_class_locked, #3292).

// Rows per request: the batch route's cap (MAX_SUBSECTION_ROWS in score-api).
const BATCH_ROWS = 100;
const DEFAULT_SPLIT_SIZE = "10";

function RiderRow({ id, label, locked, dragLabel, lifted }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: locked });
  return (
    <Box
      ref={setNodeRef}
      data-testid={`section-rider-${id}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        // The row being dragged paints above every bar it passes over (a
        // collapsed bar's orange drop hint was hiding it, real Chrome).
        position: "relative",
        zIndex: lifted ? 3 : "auto",
      }}
    >
      <IconButton
        size="small"
        ref={setActivatorNodeRef}
        disabled={locked}
        aria-label={dragLabel}
        data-testid={`section-rider-handle-${id}`}
        sx={{ touchAction: "none", cursor: locked ? "default" : "grab" }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" color="text.primary">
        {label}
      </Typography>
    </Box>
  );
}

function SectionBar({
  id,
  name,
  count,
  countLabel,
  collapsed,
  empty,
  isDropTarget,
  locked,
  onToggle,
  onRemove,
  t,
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: locked });
  return (
    <Box
      ref={setNodeRef}
      data-testid={`section-bar-${id}`}
      data-drop-target={isDropTarget ? "true" : undefined}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        outline: isDropTarget ? "3px solid" : "none",
        outlineColor: "warning.main",
        mt: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.5,
          bgcolor: "#263238",
          color: "#fff",
        }}
      >
        <IconButton
          size="small"
          ref={setActivatorNodeRef}
          disabled={locked}
          aria-label={t("classSections.dragSection", { name })}
          data-testid={`section-bar-handle-${id}`}
          sx={{ color: "#fff", touchAction: "none", cursor: locked ? "default" : "grab" }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          aria-expanded={!collapsed}
          aria-label={t(collapsed ? "classSections.expand" : "classSections.collapse", { name })}
          data-testid={`section-toggle-${id}`}
          onClick={onToggle}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
        <Typography variant="body2" sx={{ ml: 1 }} data-testid={`section-count-${id}`}>
          {countLabel}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          sx={{ color: "#fff" }}
          disabled={locked}
          aria-label={t("classSections.removeSectionAria", { name })}
          data-testid={`section-remove-${id}`}
          onClick={onRemove}
        >
          {t("classSections.removeSection")}
        </Button>
      </Box>
      {isDropTarget && (
        <Typography variant="body2" sx={{ p: 1, bgcolor: "warning.light", color: "text.primary" }}>
          {t("classSections.dropFirst")}
        </Typography>
      )}
      {empty && !collapsed && !isDropTarget && count === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 1.5, border: "1px dashed", borderColor: "divider" }}
          data-testid={`section-empty-${id}`}
        >
          {t("classSections.emptySection")}
        </Typography>
      )}
    </Box>
  );
}

function PoolHeader({ countLabel, t, active }) {
  const { setNodeRef, isOver } = useDroppable({ id: POOL_ID });
  return (
    <Box
      ref={setNodeRef}
      data-testid="section-pool-header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        px: 1.5,
        py: 0.75,
        bgcolor: "grey.200",
        color: "text.primary",
        borderBottom: 1,
        borderColor: "divider",
        outline: active && isOver ? "3px solid" : "none",
        outlineColor: "warning.main",
      }}
    >
      <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
        {t("classSections.poolHeader")}
      </Typography>
      <Typography variant="body2" component="span" sx={{ ml: 1 }} data-testid="section-pool-count">
        {countLabel}
      </Typography>
    </Box>
  );
}

export default function ClassSections() {
  const { classId } = useParams();
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const { activeShow } = useActiveShow() || {};

  const style = showSectionStyle(activeShow);
  const sectionName = useCallback(
    (n) =>
      formatSection(n, {
        label: style.label ?? t("classSections.defaultLabel"),
        numbering: style.numbering,
      }),
    [style.label, style.numbering, t],
  );

  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [cls, setCls] = useState(null);
  const [ridesById, setRidesById] = useState({});
  const [items, setItems] = useState([]);
  const [signed, setSigned] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [splitSize, setSplitSize] = useState(DEFAULT_SPLIT_SIZE);
  const [confirm, setConfirm] = useState(null); // null | "split" | "clear"
  const [saveStatus, setSaveStatus] = useState("saved"); // saved | saving | error
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  const itemsRef = useRef([]);
  const savedRef = useRef({});
  const barSeq = useRef(0);
  const chain = useRef(Promise.resolve());
  const pending = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const nextBarId = useCallback(() => {
    barSeq.current += 1;
    return makeBarId(barSeq.current);
  }, []);

  const applyLoaded = useCallback(
    (rides, isSigned) => {
      const map = {};
      for (const r of rides) map[r.id] = r;
      barSeq.current = 0;
      const built = buildItems(rides, nextBarId);
      itemsRef.current = built;
      savedRef.current = savedRowsFromRides(rides);
      setRidesById(map);
      setItems(built);
      setSigned(isSigned);
    },
    [nextBarId],
  );

  const loadAll = useCallback(async () => {
    const [klass, rides] = await Promise.all([
      scoreApi.classes.getClass(classId).then(unwrap),
      scoreApi.rides.listRides(classId).then(unwrap),
    ]);
    const list = Array.isArray(rides) ? rides : [];
    let isSigned = false;
    if (list.length) {
      const sheets = await scoreApi.scoreSheets
        .listScoreSheetsForRides({ rideIds: list.map((r) => r.id) })
        .then(unwrap);
      const authoritative = authoritativeSheetsOnly(sheets);
      isSigned = authoritative.length >= 1 && authoritative.every((s) => Boolean(s.completedAt));
    }
    return { klass, list, isSigned };
  }, [scoreApi, classId]);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    loadAll()
      .then(({ klass, list, isSigned }) => {
        if (cancelled) return;
        setCls(klass);
        applyLoaded(list, isSigned);
        setPhase("ready");
      })
      .catch(() => !cancelled && setPhase("error"));
    return () => {
      cancelled = true;
    };
  }, [loadAll, applyLoaded]);

  const runSave = useCallback(async () => {
    const next = toRows(itemsRef.current);
    const diff = diffRows(savedRef.current, next);
    if (diff.length) {
      for (let i = 0; i < diff.length; i += BATCH_ROWS) {
        await scoreApi.rides.batchSetRideSubsections({ rides: diff.slice(i, i + BATCH_ROWS) }).then(unwrap);
      }
      savedRef.current = next;
    }
  }, [scoreApi]);

  const scheduleSave = useCallback(() => {
    pending.current += 1;
    setSaveStatus("saving");
    chain.current = chain.current
      .then(runSave)
      .then(() => {
        pending.current -= 1;
        if (mounted.current && pending.current === 0) setSaveStatus("saved");
      })
      .catch(async () => {
        pending.current -= 1;
        if (!mounted.current) return;
        // Something the server refused (or a dropped connection): show the
        // server's truth again rather than a list that never saved.
        try {
          const { list, isSigned } = await loadAll();
          if (mounted.current) applyLoaded(list, isSigned);
        } catch {
          /* leave the list as is; the error banner below still says so */
        }
        if (mounted.current) setSaveStatus("error");
      });
  }, [runSave, loadAll, applyLoaded]);

  const commit = useCallback(
    (nextItems) => {
      if (nextItems === itemsRef.current) return;
      itemsRef.current = nextItems;
      setItems(nextItems);
      scheduleSave();
    },
    [scheduleSave],
  );

  const locked = signed;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_ACTIVATION }),
    useSensor(TouchSensor, { activationConstraint: TOUCH_ACTIVATION }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const view = useMemo(() => layout(items), [items]);
  const barIds = useMemo(() => view.sections.map((s) => s.id), [view]);
  const hidden = useMemo(() => {
    const out = new Set();
    for (const s of view.sections) if (collapsed.has(s.id)) for (const r of s.riderIds) out.add(r);
    return out;
  }, [view, collapsed]);
  const visibleIds = useMemo(() => items.filter((it) => !hidden.has(it.id)).map((it) => it.id), [items, hidden]);

  const activeKind = activeId ? (barIds.includes(activeId) ? "bar" : "rider") : null;
  const dropTargetBarId = activeKind === "rider" && overId && collapsed.has(overId) ? overId : null;

  const handleDragStart = ({ active }) => setActiveId(active.id);
  const handleDragOver = ({ over }) => setOverId(over ? over.id : null);
  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };
  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    setOverId(null);
    if (!over || locked) return;
    const cur = itemsRef.current;
    const dragged = cur.find((it) => it.id === active.id);
    if (!dragged) return;
    if (dragged.kind === "bar") {
      commit(moveBar(cur, active.id, over.id, collapsed));
      return;
    }
    const { items: next, openedBarId } = moveRider(cur, active.id, over.id, collapsed);
    if (openedBarId) {
      setCollapsed((prev) => {
        const n = new Set(prev);
        n.delete(openedBarId);
        return n;
      });
    }
    commit(next);
  };

  const toggleBar = (id) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const ridesList = useMemo(() => Object.values(ridesById), [ridesById]);
  const splitN = Number.parseInt(splitSize, 10);
  const splitValid = Number.isInteger(splitN) && splitN >= 1 && String(splitN) === splitSize.trim();

  const doSplit = () => {
    setConfirm(null);
    setCollapsed(new Set());
    commit(splitIntoSections(ridesList, splitN, nextBarId));
  };
  const doClear = () => {
    setConfirm(null);
    setCollapsed(new Set());
    commit(clearAll(ridesList));
  };
  const onSplitClick = () => {
    if (view.sections.length > 0) setConfirm("split");
    else doSplit();
  };

  const riderLabel = (id) => {
    const r = ridesById[id];
    return formatRideLabel(r, t("rideEditor.onHorse")) || id;
  };
  const countLabel = (n) => t("classSections.riderCount", { count: n });

  const backLink = (
    <Button component={RouterLink} to={`/classes/${classId}`} size="small" data-testid="sections-back">
      {t("classSections.backToClass")}
    </Button>
  );

  if (phase === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (phase === "error") {
    return (
      <Box sx={{ p: 2 }}>
        {backLink}
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("classSections.loadError")}
        </Alert>
      </Box>
    );
  }
  if (!cls || !isSectionTestId(cls.testId)) {
    return (
      <Box sx={{ p: 2 }}>
        {backLink}
        <Typography sx={{ mt: 2 }} color="text.secondary" data-testid="sections-not-equitation">
          {t("classSections.notEquitation")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900 }} data-testid="class-sections-page">
      {backLink}
      <Typography variant="h5" color="text.primary" sx={{ mt: 1 }}>
        {t("classSections.pageTitle")}: {classNumberLabel(cls)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("classSections.optionalHelp")}
      </Typography>

      {locked && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="sections-locked-banner">
          {t("classSections.lockedBanner")}
        </Alert>
      )}
      {saveStatus === "error" && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="sections-save-error">
          {t("classSections.saveError")}
        </Alert>
      )}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mb: 1 }}>
        <Typography variant="body2" color="text.primary">
          {t("classSections.splitPrefix")}
        </Typography>
        <TextField
          size="small"
          type="number"
          value={splitSize}
          disabled={locked}
          onChange={(e) => setSplitSize(e.target.value)}
          inputProps={{ min: 1, step: 1, "aria-label": t("classSections.splitSizeAria") }}
          sx={{ width: 90 }}
          data-testid="sections-split-size"
        />
        <Typography variant="body2" color="text.primary">
          {t("classSections.splitSuffix")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={locked || !splitValid || ridesList.length === 0}
          onClick={onSplitClick}
          data-testid="sections-split-button"
        >
          {t("classSections.split")}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={locked}
          onClick={() => commit(addBar(itemsRef.current, nextBarId()))}
          data-testid="sections-new-section"
        >
          {t("classSections.newSection")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          disabled={locked || ridesList.length === 0}
          onClick={() => setConfirm("clear")}
          data-testid="sections-clear-all"
        >
          {t("classSections.clearAll")}
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(barIds))}
          disabled={barIds.length === 0}
          data-testid="sections-collapse-all"
        >
          {t("classSections.collapseAll")}
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set())}
          disabled={barIds.length === 0}
          data-testid="sections-expand-all"
        >
          {t("classSections.expandAll")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="body2"
          color={saveStatus === "error" ? "error" : "text.secondary"}
          role="status"
          aria-live="polite"
          data-testid="sections-save-status"
        >
          {saveStatus === "saving" ? t("classSections.saving") : saveStatus === "saved" ? t("classSections.saved") : ""}
        </Typography>
      </Stack>

      {ridesList.length === 0 ? (
        <Typography color="text.secondary">{t("classSections.noRiders")}</Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box data-testid="sections-list">
            <PoolHeader
              countLabel={countLabel(view.pool.length)}
              t={t}
              active={activeId !== null}
            />
            <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
              {items.map((it) => {
                if (it.kind === "bar") {
                  const sec = view.sections.find((s) => s.id === it.id);
                  return (
                    <SectionBar
                      key={it.id}
                      id={it.id}
                      name={sectionName(sec.number)}
                      count={sec.riderIds.length}
                      countLabel={countLabel(sec.riderIds.length)}
                      collapsed={collapsed.has(it.id)}
                      empty={sec.riderIds.length === 0}
                      isDropTarget={dropTargetBarId === it.id}
                      locked={locked}
                      t={t}
                      onToggle={() => toggleBar(it.id)}
                      onRemove={() => {
                        setCollapsed((prev) => {
                          const n = new Set(prev);
                          n.delete(it.id);
                          return n;
                        });
                        commit(removeBar(itemsRef.current, it.id));
                      }}
                    />
                  );
                }
                if (hidden.has(it.id)) return null;
                return (
                  <RiderRow
                    key={it.id}
                    id={it.id}
                    label={riderLabel(it.id)}
                    locked={locked}
                    lifted={activeId === it.id}
                    dragLabel={t("classSections.dragRider", { name: riderLabel(it.id) })}
                  />
                );
              })}
            </SortableContext>
          </Box>
        </DndContext>
      )}

      <Dialog open={confirm === "split"} onClose={() => setConfirm(null)}>
        <DialogTitle>{t("classSections.confirmSplitTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("classSections.confirmSplitBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>{t("classSections.cancel")}</Button>
          <Button onClick={doSplit} variant="contained" data-testid="sections-confirm-split">
            {t("classSections.confirmSplitAction")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirm === "clear"} onClose={() => setConfirm(null)}>
        <DialogTitle>{t("classSections.confirmClearTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("classSections.confirmClearBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>{t("classSections.cancel")}</Button>
          <Button onClick={doClear} variant="contained" color="error" data-testid="sections-confirm-clear">
            {t("classSections.confirmClearAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
